"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { OfferId, StrategicOffer } from "./offerData";

const getAssetUrl = (path: string): string => {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
  const base = import.meta.env.BASE_URL || "/";
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}${path.startsWith("/") ? path.slice(1) : path}`;
};

type CloseIntent = "dismiss" | "cta";

type OfferModalProps = {
  open: boolean;
  activeOfferId: OfferId | null;
  offers: readonly StrategicOffer[];
  onSelectOffer: (offerId: OfferId | null) => void;
  onDismiss: (restoreFocus: boolean) => void;
  onRestoreFocus: () => void;
  onStartConversation: (destination: StrategicOffer["cta"]["destination"]) => void;
};

const focusableSelector = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const shouldReduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
  || document.querySelector<HTMLElement>(".stage")?.dataset.motionMode === "reduced";

type OfferOverviewSummary = {
  category: string;
  benefit: string;
  signal: string;
  signalLabel?: string;
  secondary: string;
};

const offerOverviewSummaries: Record<OfferId, OfferOverviewSummary> = {
  "sla-sandbox": {
    category: "GUARANTEED OUTCOME",
    benefit: "Prove performance before you migrate.",
    signal: "99.5%+",
    signalLabel: "accuracy commitment",
    secondary: "90-day controlled validation",
  },
  "ai-workflow-engineering": {
    category: "ZERO-CAPEX AI",
    benefit: "Bring custom AI into your operation without the upfront integration cost.",
    signal: "$25K–$50K",
    signalLabel: "Upfront integration cost absorbed by FTO",
    secondary: "$3.75 / unit hybrid outcome price",
  },
  "low-volume-coverage": {
    category: "FLAT-RATE 24/7",
    benefit: "Keep critical lines covered. Around the clock.",
    signal: "$4,000 / month",
    secondary: "Up to 3 concurrent daytime calls",
  },
  "licensed-insurance-agents": {
    category: "LICENSED CAPACITY",
    benefit: "Extend licensed capacity. Keep every interaction accountable.",
    signal: "$32 / hour",
    secondary: "P&C + L&H licensed specialists",
  },
};

export default function OfferModal({
  open,
  activeOfferId,
  offers,
  onSelectOffer,
  onDismiss,
  onRestoreFocus,
  onStartConversation,
}: OfferModalProps) {
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const closingRef = useRef(false);
  const restoreFocusRef = useRef(true);
  const dismissRef = useRef(onDismiss);
  const restoreFocusCallbackRef = useRef(onRestoreFocus);
  const startConversationRef = useRef(onStartConversation);

  const activeOffer = useMemo(
    () => offers.find(({ id }) => id === activeOfferId) ?? null,
    [activeOfferId, offers],
  );

  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    restoreFocusCallbackRef.current = onRestoreFocus;
  }, [onRestoreFocus]);

  useEffect(() => {
    startConversationRef.current = onStartConversation;
  }, [onStartConversation]);

  const requestClose = useCallback((intent: CloseIntent, destination?: "#contact") => {
    if (closingRef.current) return;
    closingRef.current = true;
    restoreFocusRef.current = intent !== "cta";
    setClosing(true);

    const completeClose = () => {
      setClosing(false);
      dismissRef.current(intent !== "cta");
      if (intent === "cta" && destination) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            startConversationRef.current(destination);
          });
        });
      }
    };

    if (shouldReduceMotion()) {
      completeClose();
      return;
    }
    closeTimerRef.current = window.setTimeout(completeClose, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    closingRef.current = false;
    restoreFocusRef.current = true;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const body = document.body;
    const root = document.documentElement;
    const background = document.querySelector<HTMLElement>(".prototype-shell");
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPaddingRight = body.style.paddingRight;
    const previousRootOverflow = root.style.overflow;
    const previousRootOverscroll = root.style.overscrollBehavior;
    const previousBackgroundInert = background?.inert ?? false;
    const previousBackgroundAriaHidden = background?.getAttribute("aria-hidden") ?? null;
    const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);

    body.setAttribute("data-offer-modal-open", "true");
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    if (background) {
      background.inert = true;
      background.setAttribute("aria-hidden", "true");
    }

    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose("dismiss");
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
      ).filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);
      if (!focusable.length) {
        event.preventDefault();
        closeButtonRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeydown);
      body.removeAttribute("data-offer-modal-open");
      body.style.overflow = previousBodyOverflow;
      body.style.paddingRight = previousBodyPaddingRight;
      root.style.overflow = previousRootOverflow;
      root.style.overscrollBehavior = previousRootOverscroll;
      if (background) {
        background.inert = previousBackgroundInert;
        if (previousBackgroundAriaHidden === null) background.removeAttribute("aria-hidden");
        else background.setAttribute("aria-hidden", previousBackgroundAriaHidden);
      }
      window.scrollTo({ left: scrollX, top: scrollY, behavior: "auto" });
      if (restoreFocusRef.current) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => restoreFocusCallbackRef.current());
        });
      }
    };
  }, [open, requestClose]);

  useEffect(() => {
    if (!open) return;
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [activeOfferId, open]);

  if (!open || typeof document === "undefined") return null;

  const titleId = activeOffer ? `offer-modal-title-${activeOffer.id}` : "offer-modal-title-overview";
  const descriptionId = activeOffer
    ? `offer-modal-description-${activeOffer.id}`
    : "offer-modal-description-overview";
  const activeOfferNumber = activeOffer
    ? String(offers.findIndex(({ id }) => id === activeOffer.id) + 1).padStart(2, "0")
    : null;
  const isSandboxOffer = activeOffer?.id === "sla-sandbox";
  const isAiWorkflowOffer = activeOffer?.id === "ai-workflow-engineering";
  const isCoverageOffer = activeOffer?.id === "low-volume-coverage";
  const isInsuranceOffer = activeOffer?.id === "licensed-insurance-agents";
  const usesExecutiveOfferLayout = isSandboxOffer || isAiWorkflowOffer || isCoverageOffer || isInsuranceOffer;
  const executiveOfferPhoto = isSandboxOffer
    ? {
        src: getAssetUrl("/offer-01-sla-sandbox.webp"),
        alt: "Operations specialists validating service performance in a controlled environment",
      }
    : isAiWorkflowOffer
      ? {
          src: getAssetUrl("/offer-02-ai-workflow.webp"),
          alt: "Workflow engineers and operations specialists collaborating on AI-enabled delivery",
        }
      : isInsuranceOffer
        ? {
            src: getAssetUrl("/offer-04-insurance-agents.webp"),
            alt: "Licensed insurance operations specialists collaborating in a professional office",
          }
        : {
            src: getAssetUrl("/offer-03-operations.webp"),
            alt: "Customer support specialists providing live operational coverage",
          };

  return createPortal(
    <div
      className="offer-modal-root"
      data-state={closing ? "closing" : "open"}
      data-accent={activeOffer?.accent ?? "overview"}
      data-offer-id={activeOffer?.id ?? "overview"}
      data-reduced-motion={shouldReduceMotion() ? "true" : "false"}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose("dismiss");
      }}
    >
      <section
        className="offer-modal-panel"
        id="strategic-offers-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        ref={dialogRef}
      >
        <div className="offer-modal-grid" aria-hidden="true" />
        <button
          className="offer-modal-close"
          type="button"
          aria-label="Close strategic offer"
          onClick={() => requestClose("dismiss")}
          ref={closeButtonRef}
        >
          <span aria-hidden="true">×</span>
        </button>

        {activeOffer ? (
          <div className="offer-modal-detail" data-offer-id={activeOffer.id}>
            {usesExecutiveOfferLayout ? (
              <div className="offer-coverage-layout">
                <header className="offer-coverage-header">
                  <div className="offer-coverage-lead">
                    <div className="offer-coverage-hero-copy">
                      <div className="offer-coverage-meta">
                        <span className="offer-modal-label">STRATEGIC OFFER {activeOfferNumber}</span>
                        {activeOffer.content.badge && (
                          <span className="offer-coverage-badge">{activeOffer.content.badge}</span>
                        )}
                      </div>
                      <div className="offer-coverage-heading">
                        <h2 id={titleId}>{activeOffer.title}</h2>
                        {activeOffer.subtitle && (
                          <p className="offer-modal-subtitle">{activeOffer.subtitle}</p>
                        )}
                      </div>
                      <div className="offer-modal-copy" id={descriptionId}>
                        {activeOffer.content.intro && <p>{activeOffer.content.intro}</p>}
                        {activeOffer.content.paragraphs.map((paragraph) => (
                          <p key={paragraph}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                    <figure className="offer-coverage-photo">
                      <img
                        src={executiveOfferPhoto.src}
                        alt={executiveOfferPhoto.alt}
                        width="1536"
                        height="1024"
                      />
                    </figure>
                  </div>
                </header>

                {activeOffer.content.metrics && activeOffer.content.metrics.length > 0 && (
                  <section className="offer-coverage-section" aria-labelledby="offer-coverage-metrics-title">
                    <h3 id="offer-coverage-metrics-title">OFFER AT A GLANCE</h3>
                    <ul className="offer-coverage-metrics">
                      {activeOffer.content.metrics.map((metric) => (
                        <li data-accent={metric.accent ? "true" : "false"} key={metric.label}>
                          <strong>{metric.value}</strong>
                          <span>{metric.label}</span>
                        </li>
                      ))}
                    </ul>
                    {activeOffer.content.metricsNote && (
                      <p className="offer-coverage-metrics-note">{activeOffer.content.metricsNote}</p>
                    )}
                  </section>
                )}

                {activeOffer.content.comparison && (
                  <section className="offer-coverage-section" aria-labelledby="offer-coverage-comparison-title">
                    <h3 id="offer-coverage-comparison-title">{activeOffer.content.comparison.label}</h3>
                    <div className="offer-coverage-comparison">
                      <article className="offer-coverage-option offer-coverage-option-baseline">
                        <h4>{activeOffer.content.comparison.baseline.title}</h4>
                        {activeOffer.content.comparison.baseline.details.map((detail) => (
                          <p key={detail}>{detail}</p>
                        ))}
                      </article>
                      <span className="offer-coverage-transfer" aria-hidden="true">→</span>
                      <article className="offer-coverage-option offer-coverage-option-fto">
                        <div className="offer-coverage-option-main">
                          <h4>{activeOffer.content.comparison.offer.title}</h4>
                          {activeOffer.content.comparison.offer.details ? (
                            <>
                              {activeOffer.content.comparison.offer.details.map((detail) => (
                                <p key={detail}>{detail}</p>
                              ))}
                              <strong>{activeOffer.content.comparison.offer.price}</strong>
                            </>
                          ) : (
                            <>
                              <strong>{activeOffer.content.comparison.offer.price}</strong>
                              {activeOffer.content.comparison.offer.description && (
                                <p>{activeOffer.content.comparison.offer.description}</p>
                              )}
                            </>
                          )}
                        </div>
                        <span className="offer-coverage-saving">
                          {activeOffer.content.comparison.offer.saving}
                        </span>
                      </article>
                    </div>
                    {activeOffer.content.comparison.note && (
                      <p className="offer-coverage-comparison-note">
                        {activeOffer.content.comparison.note}
                      </p>
                    )}
                  </section>
                )}

                {activeOffer.content.useCases && activeOffer.content.useCases.length > 0 && (
                  <section className="offer-coverage-section" aria-labelledby="offer-coverage-use-cases-title">
                    <h3 id="offer-coverage-use-cases-title">BUILT FOR</h3>
                    <ul className="offer-coverage-use-cases">
                      {activeOffer.content.useCases.map((useCase, index) => (
                        <li key={useCase.title}>
                          {(isSandboxOffer || isAiWorkflowOffer || isInsuranceOffer) && (
                            <span className="offer-coverage-use-case-index" aria-hidden="true">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                          )}
                          <strong>{useCase.title}</strong>
                          <span>{useCase.description}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="offer-coverage-footer">
                  <button
                    className="offer-modal-cta"
                    type="button"
                    onClick={() => requestClose("cta", activeOffer.cta.destination)}
                  >
                    <span>{activeOffer.cta.label}</span><i aria-hidden="true">→</i>
                  </button>
                  <button
                    className="offer-coverage-back"
                    type="button"
                    onClick={() => onSelectOffer(null)}
                  >
                    Back to Strategic Offers
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="offer-modal-label">STRATEGIC OFFER</span>
                <h2 id={titleId}>{activeOffer.title}</h2>
                {activeOffer.subtitle && <p className="offer-modal-subtitle">{activeOffer.subtitle}</p>}
                <div className="offer-modal-copy" id={descriptionId}>
                  {activeOffer.content.intro && <p>{activeOffer.content.intro}</p>}
                  {activeOffer.content.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {activeOffer.content.bulletPoints && activeOffer.content.bulletPoints.length > 0 && (
                    <ul>{activeOffer.content.bulletPoints.map((item) => <li key={item}>{item}</li>)}</ul>
                  )}
                  {activeOffer.content.conditions && activeOffer.content.conditions.length > 0 && (
                    <div className="offer-modal-conditions">
                      {activeOffer.content.conditions.map((condition) => <p key={condition}>{condition}</p>)}
                    </div>
                  )}
                </div>
                <button
                  className="offer-modal-cta"
                  type="button"
                  onClick={() => requestClose("cta", activeOffer.cta.destination)}
                >
                  <span>{activeOffer.cta.label}</span><i aria-hidden="true">→</i>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="offer-modal-overview">
            <header className="offer-overview-header">
              <span className="offer-modal-label">STRATEGIC OFFERS</span>
              <h2 id={titleId}>Four focused offers. Built around real operational needs.</h2>
              <p id={descriptionId}>
                Validate performance, deploy custom AI, maintain 24/7 coverage, or add licensed
                insurance capacity—with a model designed around the specific challenge you need to solve.
              </p>
            </header>
            <ul className="offer-overview-grid">
              {offers.map((offer, index) => {
                const overview = offerOverviewSummaries[offer.id];
                return (
                  <li key={offer.id}>
                    <button
                      className="offer-overview-card"
                      type="button"
                      data-accent={offer.accent}
                      data-offer-id={offer.id}
                      aria-label={`View ${offer.title} offer details`}
                      onClick={() => onSelectOffer(offer.id)}
                    >
                      <span className="offer-overview-card-accent" aria-hidden="true" />
                      <span className="offer-overview-card-meta">
                        <b>{String(index + 1).padStart(2, "0")}</b>
                        <span>{overview.category}</span>
                      </span>
                      <strong className="offer-overview-card-title">{offer.title}</strong>
                      <span className="offer-overview-card-benefit">{overview.benefit}</span>
                      <span className="offer-overview-card-signal">
                        <strong>{overview.signal}</strong>
                        {overview.signalLabel && <span>{overview.signalLabel}</span>}
                      </span>
                      <span className="offer-overview-card-secondary">{overview.secondary}</span>
                      <span className="offer-overview-card-cta">
                        VIEW OFFER <i aria-hidden="true">→</i>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
}
